import { Component, Input, OnInit } from '@angular/core';
import { Validators, FormBuilder } from '@angular/forms';
import {
  dateValidator,
  dateBeforeValidator,
  dateAfterValidator,
  ageMinValidator,
  ageMaxValidator,
  emailAddressValidator
} from '@liberty-financial/ng-core';
import { diff } from 'deep-object-diff';
import { forkJoin, Observable, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { ContactSummary } from '@features/contacts/models/contactsummary';
import { Individual } from '@features/contacts/models/individual';
import { Dependent } from '@features/contacts/models/individual/dependent';
import { Relationship } from '@features/contacts/models/individual/relationship';
import { Residence } from '@features/contacts/models/individual/residence';
import { ContactReferrerArrangement } from '@features/contacts/models/referrer-arrangement';
import { IndividualService } from '@features/contacts/services/individual.service';
import { TagEntityType } from '@features/opportunities/services/tag.service';
import { Tag } from '@shared/models/tag';
import { SelectListOption } from '@shared/components/form-fields/select-list-option';
import {
  CommandEvent,
  commandEvent
} from '@shared/components/load/command/command.component';
import { StandardSchemaUtil } from '@shared/utilities/standard-schema-util';
import { ContactType } from '@features/contacts/models';
import { Aggregator } from '@liberty-financial/agp-shared';
import { aggregator } from '@env/aggregator/aggregator';
import { AGE_MINIMUM, AGE_MAXIMUM } from '@shared/constants/age.constants';

export interface SchemaDefinition {
  individual: Individual.SchemaPropertyDefinition;
  dependents: Dependent.SchemaPropertyDefinition;
  relationships: Relationship.SchemaPropertyDefinition;
  residences: Residence.SchemaPropertyDefinition;
}

export interface Content {
  individual: Individual.Model;
  contacts: ContactSummary[];
}

export type LoadContent = {
  schema: SchemaDefinition;
  content: Content;
};

@Component({ template: '' })
export abstract class IndividualDetailsComponent implements OnInit {
  @Input() cancel: () => void = () => {};
  @Input() saved: () => void = () => {};

  individualId!: string;
  selectedContact: ContactSummary | null = null;

  public readonly minAge: number = AGE_MINIMUM;
  public readonly maxAge: number = AGE_MAXIMUM;

  abstract get title(): string;
  abstract get saveLabel(): string;
  abstract readonly isNewEntity: boolean;
  abstract content(): Observable<Content>;

  tagEntityType: TagEntityType = TagEntityType.Individuals;
  pendingTags: Tag[] = [];

  driverLicenceTypes: SelectListOption[] = [
    { value: 'Full', label: 'Full' },
    { value: 'Provisional', label: 'Provisional' },
    { value: 'Learners', label: 'Learners' }
  ];

  private loadSchema$: Observable<SchemaDefinition> = forkJoin([
    this.individualService.getIndividualDefinition(),
    this.individualService.getDependentDefinition(),
    this.individualService.getRelationshipDefinition(),
    this.individualService.getResidenceDefinition()
  ]).pipe(
    map(([individual, dependents, relationships, residences]) => ({
      individual,
      dependents,
      relationships,
      residences
    }))
  );
  public load$!: Observable<LoadContent>;
  public load!: LoadContent;
  protected change$: Subject<any> = new Subject<any>();
  public update$: Observable<CommandEvent<object>> = commandEvent(
    this.change$,
    (x) => this.individualService.updateIndividual(this.individualId, x)
  );

  citizenLabel = $localize`:Citizen Label|Name of the citizen of a region@@citizen-label:Australian citizen`;

  formGroup = this.fb.group({
    name: this.fb.group({
      title: [null as string | null],
      first: this.fb.nonNullable.control('', [Validators.maxLength(50)]),
      last: this.fb.nonNullable.control('', [Validators.maxLength(50)]),
      middle: [null as string | null],
      preferred: [null as string | null]
    }),
    gender: this.fb.control(null as string | null),
    birthDate: this.fb.control(null as string | null, [
      dateValidator(),
      dateBeforeValidator(),
      ageMinValidator(AGE_MINIMUM),
      ageMaxValidator(AGE_MAXIMUM)
    ]),
    contactType: [null as ContactType | null],
    status: [null as string[] | null],
    sendMarketingCommunications: [(aggregator.name !== Aggregator.NMB) as boolean],
    nmbAffiliateId: [null as string | null],
    contact: this.fb.group({
      homePhone: [null as string | null],
      workPhone: [null as string | null],
      mobile: [null as string | null],
      email: [
        null as string | null,
        [emailAddressValidator(), Validators.maxLength(256)]
      ],
      preferredMethod: [null as string | null, [Validators.maxLength(50)]]
    }),

    identification: this.fb.group({
      driverLicence: this.fb.group({
        number: [null as string | null],
        cardNumber: [null as string | null],
        expiry: [null as string | null, [dateValidator, dateAfterValidator]],
        type: [null as string | null],
        issuer: [null as string | null]
      }),
      passport: this.fb.group({
        number: [null as string | null],
        expiry: [null as string | null, [dateValidator, dateAfterValidator]]
      })
    }),
    bankingInstitution: [null as string | null],
    permanentResident: [null as boolean | null],
    citizen: [null as boolean | null],
    repayingStudentLoan: [null as boolean | null],
    referrerArrangement: [null as ContactReferrerArrangement.Model | null],
    dependents: [[] as Dependent.Model[]],
    residences: [[] as Residence.Model[]],
    relationships: [[] as Relationship.Model[]]
  });

  constructor(
    private fb: FormBuilder,
    protected individualService: IndividualService
  ) {}

  ngOnInit(): void {
    this.load$ = forkJoin([this.loadSchema$, this.content()]).pipe(
      map(([schema, content]) => ({ schema, content })),
      tap((x) => {
        StandardSchemaUtil.attachFormGroupToSchema(
          this.formGroup,
          x.schema.individual
        );
        this.formGroup.patchValue(x.content.individual as any);
        this.load = x;
      })
    );
  }

  onTagsChanged(tags: Tag[]): void {
    this.pendingTags = tags;
  }

  public save() {
    this.formGroup.markAllAsTouched();

    if (this.formGroup.invalid) {
      return;
    }
    const formValue = this.formGroup.getRawValue();
    
    // Clear referrerArrangement if contact type is not Referrer
    if (formValue.contactType !== ContactType.Referrer) {
      formValue.referrerArrangement = null;
    }
    
    let delta = diff(
      this.load.content.individual,
      formValue
    ) as Partial<Individual.Model>;

    if (delta.dependents) {
      delta.dependents =
        (formValue.dependents as Dependent.Model[]) || undefined;
    }

    if (delta.relationships) {
      delta.relationships =
        (formValue.relationships as Relationship.Model[]) || undefined;
    }

    if (delta.residences) {
      delta.residences =
        (formValue.residences as Residence.Model[]) || undefined;
    }

    if (delta.referrerArrangement) {
      delta.referrerArrangement =
        (formValue.referrerArrangement as ContactReferrerArrangement.Model) ||
        null;
    }

    this.change$.next(delta);
  }
}
